import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, ViewEncapsulation, Component, ViewChild, NgModule } from '@angular/core';
import { E11TemplateMap, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { groupByKey } from '@engineering11/utility';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { NavigationEnd, NavigationStart, RouterModule } from '@angular/router';
import { filter } from 'rxjs';

function e11MenuItem(name, icon, isVisible, routeHref, navigationExtras, position, children, isOpen, customTemplateKey, onClick) {
    return {
        id: `${name.toLowerCase()}-group-id`,
        name,
        route: routeHref,
        navigationExtras,
        icon,
        isOpen,
        children,
        customTemplateKey,
        position,
        isVisible,
        onClick,
    };
}
function e11MenuItemWithRouter(...args) {
    const [name, route, icon, isVisible, position, children] = args;
    return {
        ...e11MenuItem(name, icon, isVisible, route, undefined, position, children),
    };
}
function e11MenuItemWithRouterWithAction(onClick, ...args) {
    return {
        ...e11MenuItemWithRouter(...args),
        onClick,
    };
}
function e11MenuItemWithTemplateKey(name, customTemplateKey, isVisible, position) {
    return {
        ...e11MenuItem(name, undefined, isVisible),
        position,
        customTemplateKey,
    };
}
/**
 * Deterministic function to know the ID for a nav item
 * @param navItem
 * @returns
 */
function idForNavItem(navItem) {
    const idPrefix = navItem.children?.length ? 'group-id' : 'nav-id';
    // TODO: consider stripping spaces and figure out how to make this distinct from other IDs of same name - possibly hash entire item?
    return navItem.id ?? `${idPrefix}-${navItem.name.toLowerCase()}`;
}

class E11NavItemComponent {
    set nav(navItem) {
        if (typeof navItem.isOpen === 'function') {
            this.isOpenFunction = navItem.isOpen;
            navItem.isOpen = this.isOpenFunction(this.router.url);
        }
        this.navItem = { id: idForNavItem(navItem), ...navItem };
    }
    set iconOnly(value) {
        this._iconOnly = value;
        if (value) {
            this.navItem.isOpen = false;
        }
    }
    constructor(router, activatedRoute, cdRef) {
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.cdRef = cdRef;
        this.e11TemplateMap = {};
        this.isChildren = false;
        this._iconOnly = false;
        this.close = new EventEmitter();
    }
    ngOnInit() {
        this.setupRouteWatcher();
    }
    setupRouteWatcher() {
        this.formattedRoute = Array.isArray(this.navItem.route) ? this.navItem.route.join('/') : this.navItem.route;
        this.router.events
            .pipe(filter((_) => Array.isArray(this.navItem.children)), filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
            .subscribe({
            next: (currentRoute) => {
                this.handleRouteChange(currentRoute);
            },
        });
    }
    handleRouteChange(currentRoute) {
        const currentUrl = currentRoute.url;
        if (typeof this.isOpenFunction === 'function') {
            this.navItem.isOpen = this.isOpenFunction(currentUrl.toLowerCase());
            return;
        }
        // default route state manager
        if (this.formattedRoute && currentUrl.toLowerCase().startsWith(this.formattedRoute)) {
            this.navItem.isOpen = true;
        }
        this.cdRef.detectChanges();
    }
    onClick(item) {
        if (item.onClick) {
            item.onClick();
        }
        if (item.children && item.children.length) {
            item.isOpen = !item.isOpen;
        }
        else {
            this.close.emit();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavItemComponent, deps: [{ token: i1.Router }, { token: i1.ActivatedRoute }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11NavItemComponent, isStandalone: true, selector: "e11-nav-item", inputs: { e11TemplateMap: "e11TemplateMap", nav: "nav", isChildren: "isChildren", iconOnly: "iconOnly" }, outputs: { close: "close" }, ngImport: i0, template: `
    <ng-container *ngIf="!navItem.isVisible || (navItem.isVisible | async)">
      <ng-container *ngIf="!navItem.customTemplateKey">
        <a
          (click)="onClick(navItem)"
          class="e11-flex e11-items-center e11-cursor-pointer hover:e11-text-white e11-px-2 e11-rounded-md"
          [id]="navItem.id"
          [ngClass]="{
            'e11-h-12 e11-justify-between': !isChildren,
            'e11-h-10 e11-pl-2 e11-truncate e11-my-2': isChildren,
            'e11-bg-skin-white/20 e11-border-b-transparent e11-text-white': navItem.isOpen
          }"
          [routerLink]="navItem.route"
          [queryParams]="navItem.navigationExtras?.queryParams"
          [relativeTo]="navItem.navigationExtras?.relativeTo"
          [fragment]="navItem.navigationExtras?.fragment"
          [preserveFragment]="navItem.navigationExtras?.preserveFragment"
          [queryParamsHandling]="navItem.navigationExtras?.queryParamsHandling"
          [replaceUrl]="navItem.navigationExtras?.replaceUrl"
          [skipLocationChange]="navItem.navigationExtras?.skipLocationChange"
          [state]="navItem.navigationExtras?.state"
          [routerLinkActive]="'e11-bg-skin-primary-accent e11-text-skin-white'"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          <div class="e11-flex e11-items-center e11-w-full" [ngClass]="{ 'e11-justify-center': _iconOnly }">
            <span *ngIf="navItem.icon" class="material-icons " [ngClass]="{ 'md-24': _iconOnly, 'md-18 e11-mr-2': !_iconOnly }">
              {{ navItem.icon }}
            </span>
            <span *ngIf="!_iconOnly" class="e11-relative">{{ navItem.name }}</span>
          </div>
          <span
            *ngIf="!_iconOnly && navItem.children?.length"
            class="material-icons-outlined e11-text-skin-extra-light nav-icon"
            [ngClass]="{ 'nav-icon-active': navItem.isOpen }"
          >
            expand_more
          </span>
        </a>
        <div
          *ngIf="navItem.children && navItem.children.length && !_iconOnly"
          class="nav-children-container e11-px-2"
          [ngClass]="{ 'nav-children-container-active': navItem.isOpen }"
        >
          <div class="e11-my-2">
            <e11-nav-item
              *ngFor="let child of navItem.children"
              [nav]="child"
              [isChildren]="true"
              [e11TemplateMap]="e11TemplateMap"
              (close)="onClick(navItem)"
            ></e11-nav-item>
          </div>
        </div>
      </ng-container>
      <ng-container *ngIf="navItem.customTemplateKey && e11TemplateMap">
        <ng-container *ngTemplateOutlet="e11TemplateMap[navItem.customTemplateKey]"></ng-container>
      </ng-container>
    </ng-container>
  `, isInline: true, styles: [".nav-children-container{max-height:0;overflow:hidden;transition:max-height .15s ease-in-out 0s}.nav-children-container.nav-children-container-active{height:auto;max-height:2000px;transition:max-height .15s ease-in-out 0s}.nav-icon{transform:rotate(0);transition:transform .1s ease-in-out 0s}.nav-icon.nav-icon-active{transform:rotate(180deg);transition:transform .2s ease-in-out 0s}\n"], dependencies: [{ kind: "component", type: E11NavItemComponent, selector: "e11-nav-item", inputs: ["e11TemplateMap", "nav", "isChildren", "iconOnly"], outputs: ["close"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i1.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavItemComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'e11-nav-item', imports: [CommonModule, RouterModule], template: `
    <ng-container *ngIf="!navItem.isVisible || (navItem.isVisible | async)">
      <ng-container *ngIf="!navItem.customTemplateKey">
        <a
          (click)="onClick(navItem)"
          class="e11-flex e11-items-center e11-cursor-pointer hover:e11-text-white e11-px-2 e11-rounded-md"
          [id]="navItem.id"
          [ngClass]="{
            'e11-h-12 e11-justify-between': !isChildren,
            'e11-h-10 e11-pl-2 e11-truncate e11-my-2': isChildren,
            'e11-bg-skin-white/20 e11-border-b-transparent e11-text-white': navItem.isOpen
          }"
          [routerLink]="navItem.route"
          [queryParams]="navItem.navigationExtras?.queryParams"
          [relativeTo]="navItem.navigationExtras?.relativeTo"
          [fragment]="navItem.navigationExtras?.fragment"
          [preserveFragment]="navItem.navigationExtras?.preserveFragment"
          [queryParamsHandling]="navItem.navigationExtras?.queryParamsHandling"
          [replaceUrl]="navItem.navigationExtras?.replaceUrl"
          [skipLocationChange]="navItem.navigationExtras?.skipLocationChange"
          [state]="navItem.navigationExtras?.state"
          [routerLinkActive]="'e11-bg-skin-primary-accent e11-text-skin-white'"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          <div class="e11-flex e11-items-center e11-w-full" [ngClass]="{ 'e11-justify-center': _iconOnly }">
            <span *ngIf="navItem.icon" class="material-icons " [ngClass]="{ 'md-24': _iconOnly, 'md-18 e11-mr-2': !_iconOnly }">
              {{ navItem.icon }}
            </span>
            <span *ngIf="!_iconOnly" class="e11-relative">{{ navItem.name }}</span>
          </div>
          <span
            *ngIf="!_iconOnly && navItem.children?.length"
            class="material-icons-outlined e11-text-skin-extra-light nav-icon"
            [ngClass]="{ 'nav-icon-active': navItem.isOpen }"
          >
            expand_more
          </span>
        </a>
        <div
          *ngIf="navItem.children && navItem.children.length && !_iconOnly"
          class="nav-children-container e11-px-2"
          [ngClass]="{ 'nav-children-container-active': navItem.isOpen }"
        >
          <div class="e11-my-2">
            <e11-nav-item
              *ngFor="let child of navItem.children"
              [nav]="child"
              [isChildren]="true"
              [e11TemplateMap]="e11TemplateMap"
              (close)="onClick(navItem)"
            ></e11-nav-item>
          </div>
        </div>
      </ng-container>
      <ng-container *ngIf="navItem.customTemplateKey && e11TemplateMap">
        <ng-container *ngTemplateOutlet="e11TemplateMap[navItem.customTemplateKey]"></ng-container>
      </ng-container>
    </ng-container>
  `, encapsulation: ViewEncapsulation.None, styles: [".nav-children-container{max-height:0;overflow:hidden;transition:max-height .15s ease-in-out 0s}.nav-children-container.nav-children-container-active{height:auto;max-height:2000px;transition:max-height .15s ease-in-out 0s}.nav-icon{transform:rotate(0);transition:transform .1s ease-in-out 0s}.nav-icon.nav-icon-active{transform:rotate(180deg);transition:transform .2s ease-in-out 0s}\n"] }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: i1.ActivatedRoute }, { type: i0.ChangeDetectorRef }], propDecorators: { e11TemplateMap: [{
                type: Input
            }], nav: [{
                type: Input
            }], isChildren: [{
                type: Input
            }], iconOnly: [{
                type: Input
            }], close: [{
                type: Output
            }] } });

class E11NavLeftComponent extends E11TemplateMap {
    constructor() {
        super(...arguments);
        this.mobileSideDrawerToggled = false;
        this.mobileTabs = [];
        this.iconOnly = false;
        this.mobileSideDrawerToggledChange = new EventEmitter();
        this.selectedTab = 'main';
        this.groupedNavigationItems = {
            main: [],
            footer: [],
        };
        this.navLeftBottomPos = 0;
        this.navigationContainerTopPadding = 104; // Header height plus 1.5 rem
    }
    set navigationList(items) {
        const itemsWithDefaults = items.map((item) => (item.position ? item : { ...item, position: 'main' }));
        this.groupedNavigationItems = groupByKey(itemsWithDefaults, 'position');
    }
    close() {
        if (this.mobileSideDrawerToggled)
            this.mobileSideDrawerToggledChange.emit(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11NavLeftComponent, selector: "e11-nav-left", inputs: { navigationList: "navigationList", mobileSideDrawerToggled: "mobileSideDrawerToggled", mobileTabs: "mobileTabs", iconOnly: "iconOnly" }, outputs: { mobileSideDrawerToggledChange: "mobileSideDrawerToggledChange" }, viewQueries: [{ propertyName: "navContainer", first: true, predicate: ["navContainer"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"e11-hidden lg:e11-flex e11-text-skin-light e11-flex-col e11-h-full lg:e11-divide-y lg:e11-divide-skin-grey/30\">\n  <!-- Top -->\n  <div\n    class=\"e11-flex-grow e11-relative e11-overscroll-contain e11-overflow-y-scroll e11-scrollbar-thumb-blue e11-scrollbar-thumb-rounded e11-scrollbar-track-blue-lighter e11-scrollbar-w-2 e11-scrolling-touch e11-divide-y e11-divide-skin-grey/30\"\n  >\n    <ng-container *ngFor=\"let nav of groupedNavigationItems | keyvalue\">\n      <ng-container *ngIf=\"nav.key !== 'footer'\">\n        <div *ngFor=\"let nav of nav.value\">\n          <e11-nav-item\n            *ngIf=\"!nav.customTemplateKey\"\n            [nav]=\"nav\"\n            [e11TemplateMap]=\"e11TemplateMap\"\n            (close)=\"close()\"\n            [iconOnly]=\"iconOnly\"\n          ></e11-nav-item>\n          <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n            <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n          </ng-container>\n        </div>\n      </ng-container>\n    </ng-container>\n  </div>\n  <!-- bottom -->\n  <div\n    *ngIf=\"groupedNavigationItems.footer && groupedNavigationItems.footer.length\"\n    class=\"e11-flex e11-flex-col e11-font-bold e11-cursor-pointer e11-divide-y e11-divide-skin-grey/30\"\n  >\n    <div *ngFor=\"let nav of groupedNavigationItems.footer\">\n      <e11-nav-item\n        *ngIf=\"!nav.customTemplateKey\"\n        [nav]=\"nav\"\n        [e11TemplateMap]=\"e11TemplateMap\"\n        (close)=\"close()\"\n        [iconOnly]=\"iconOnly\"\n      ></e11-nav-item>\n      <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"mobileSideDrawerToggled\" class=\"e11-divide-y e11-h-full e11-divide-gray-200 e11-flex e11-flex-col\">\n  <!-- content-->\n  <div class=\"e11-text-white e11-flex-1 e11-px-6 e11-overflow-y-auto e11-overscroll-none e11-flex e11-flex-col e11-divide-y e11-divide-skin-grey/30\">\n    <ng-container *ngFor=\"let tab of mobileTabs\">\n      <ng-container *ngIf=\"tab.routeGroup === selectedTab\">\n        <div *ngFor=\"let nav of groupedNavigationItems[tab.routeGroup]\" class=\"\">\n          <div>\n            <e11-nav-item *ngIf=\"!nav.customTemplateKey\" [nav]=\"nav\" [e11TemplateMap]=\"e11TemplateMap\" (close)=\"close()\"></e11-nav-item>\n            <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n              <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n            </ng-container>\n          </div>\n        </div>\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <!-- Tabs-->\n  <div class=\"e11-text-skin-light e11-flex e11-justify-between e11-px-6 e11-py-4 e11-w-full\">\n    <div\n      *ngFor=\"let tab of mobileTabs\"\n      (click)=\"selectedTab = tab.routeGroup\"\n      class=\"e11-flex e11-flex-col e11-items-center\"\n      [ngClass]=\"{ 'e11-text-white': selectedTab === tab.routeGroup }\"\n    >\n      <span class=\"icon material-icons\">{{ tab.icon }}</span>\n      <span>{{ tab.name }}</span>\n    </div>\n\n    <div (click)=\"close()\" class=\"e11-flex e11-flex-col e11-items-center\">\n      <span class=\"icon material-icons material-icons-outlined\">arrow_back_ios</span>\n      <span>{{ 'Close' }}</span>\n    </div>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: E11NavItemComponent, selector: "e11-nav-item", inputs: ["e11TemplateMap", "nav", "isChildren", "iconOnly"], outputs: ["close"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1$1.KeyValuePipe, name: "keyvalue" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-nav-left', encapsulation: ViewEncapsulation.None, template: "<div class=\"e11-hidden lg:e11-flex e11-text-skin-light e11-flex-col e11-h-full lg:e11-divide-y lg:e11-divide-skin-grey/30\">\n  <!-- Top -->\n  <div\n    class=\"e11-flex-grow e11-relative e11-overscroll-contain e11-overflow-y-scroll e11-scrollbar-thumb-blue e11-scrollbar-thumb-rounded e11-scrollbar-track-blue-lighter e11-scrollbar-w-2 e11-scrolling-touch e11-divide-y e11-divide-skin-grey/30\"\n  >\n    <ng-container *ngFor=\"let nav of groupedNavigationItems | keyvalue\">\n      <ng-container *ngIf=\"nav.key !== 'footer'\">\n        <div *ngFor=\"let nav of nav.value\">\n          <e11-nav-item\n            *ngIf=\"!nav.customTemplateKey\"\n            [nav]=\"nav\"\n            [e11TemplateMap]=\"e11TemplateMap\"\n            (close)=\"close()\"\n            [iconOnly]=\"iconOnly\"\n          ></e11-nav-item>\n          <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n            <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n          </ng-container>\n        </div>\n      </ng-container>\n    </ng-container>\n  </div>\n  <!-- bottom -->\n  <div\n    *ngIf=\"groupedNavigationItems.footer && groupedNavigationItems.footer.length\"\n    class=\"e11-flex e11-flex-col e11-font-bold e11-cursor-pointer e11-divide-y e11-divide-skin-grey/30\"\n  >\n    <div *ngFor=\"let nav of groupedNavigationItems.footer\">\n      <e11-nav-item\n        *ngIf=\"!nav.customTemplateKey\"\n        [nav]=\"nav\"\n        [e11TemplateMap]=\"e11TemplateMap\"\n        (close)=\"close()\"\n        [iconOnly]=\"iconOnly\"\n      ></e11-nav-item>\n      <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"mobileSideDrawerToggled\" class=\"e11-divide-y e11-h-full e11-divide-gray-200 e11-flex e11-flex-col\">\n  <!-- content-->\n  <div class=\"e11-text-white e11-flex-1 e11-px-6 e11-overflow-y-auto e11-overscroll-none e11-flex e11-flex-col e11-divide-y e11-divide-skin-grey/30\">\n    <ng-container *ngFor=\"let tab of mobileTabs\">\n      <ng-container *ngIf=\"tab.routeGroup === selectedTab\">\n        <div *ngFor=\"let nav of groupedNavigationItems[tab.routeGroup]\" class=\"\">\n          <div>\n            <e11-nav-item *ngIf=\"!nav.customTemplateKey\" [nav]=\"nav\" [e11TemplateMap]=\"e11TemplateMap\" (close)=\"close()\"></e11-nav-item>\n            <ng-container *ngIf=\"nav.customTemplateKey && (!nav.isVisible || (nav.isVisible | async))\">\n              <ng-container *ngTemplateOutlet=\"e11TemplateMap[nav.customTemplateKey]\"></ng-container>\n            </ng-container>\n          </div>\n        </div>\n      </ng-container>\n    </ng-container>\n  </div>\n\n  <!-- Tabs-->\n  <div class=\"e11-text-skin-light e11-flex e11-justify-between e11-px-6 e11-py-4 e11-w-full\">\n    <div\n      *ngFor=\"let tab of mobileTabs\"\n      (click)=\"selectedTab = tab.routeGroup\"\n      class=\"e11-flex e11-flex-col e11-items-center\"\n      [ngClass]=\"{ 'e11-text-white': selectedTab === tab.routeGroup }\"\n    >\n      <span class=\"icon material-icons\">{{ tab.icon }}</span>\n      <span>{{ tab.name }}</span>\n    </div>\n\n    <div (click)=\"close()\" class=\"e11-flex e11-flex-col e11-items-center\">\n      <span class=\"icon material-icons material-icons-outlined\">arrow_back_ios</span>\n      <span>{{ 'Close' }}</span>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { navContainer: [{
                type: ViewChild,
                args: ['navContainer']
            }], navigationList: [{
                type: Input
            }], mobileSideDrawerToggled: [{
                type: Input
            }], mobileTabs: [{
                type: Input
            }], iconOnly: [{
                type: Input
            }], mobileSideDrawerToggledChange: [{
                type: Output
            }] } });

class E11NavLeftModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftModule, declarations: [E11NavLeftComponent], imports: [CommonModule, RouterModule, E11UtilModule, E11NavItemComponent], exports: [RouterModule, E11UtilModule, E11NavLeftComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftModule, imports: [CommonModule, RouterModule, E11UtilModule, E11NavItemComponent, RouterModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NavLeftModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11NavLeftComponent],
                    imports: [CommonModule, RouterModule, E11UtilModule, E11NavItemComponent],
                    exports: [RouterModule, E11UtilModule, E11NavLeftComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11NavItemComponent, E11NavLeftComponent, E11NavLeftModule, e11MenuItem, e11MenuItemWithRouter, e11MenuItemWithRouterWithAction, e11MenuItemWithTemplateKey, idForNavItem };
